import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { PageHeader, Loader, StatCard } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Banknote, TrendingUp, AlertTriangle, ShieldCheck, Users2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function PayrollDashboard() {
  const navigate = useNavigate();
  const [periode, setPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const [gen, setGen] = useState(false);

  const load = () => { setD(null); api.get(`/payroll/dashboard?periode=${periode}`).then((r) => setD(r.data)).catch(() => setD({})); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode]);

  const generateContribution = async () => {
    setGen(true);
    try {
      const r = await api.post("/payroll/contributions/generate", { periode, type: "CNSS" });
      toast.success(`Cotisation ${r.data.numero} générée : ${fmtMoney(r.data.montant)}`);
      navigate("/cotisations");
    } catch (e) { toast.error(apiErr(e)); } finally { setGen(false); }
  };

  return (
    <div>
      <PageHeader title="Tableau de bord — Paie" subtitle="Masse salariale, nets payés, restant dû & cotisations à reverser">
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Période</Label>
          <Input type="month" value={periode} onChange={(e) => setPeriode(e.target.value)} className="h-9 w-40" data-testid="payroll-period" />
        </div>
      </PageHeader>

      {d === null ? <Loader /> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <StatCard label="Masse salariale (brut)" value={fmtMoney(d.masse_salariale_brute)} icon={TrendingUp} accent="blue" hint={`${d.nb_bulletins || 0} bulletin(s) · ${d.contracts_actifs || 0} contrat(s) actif(s)`} />
            <StatCard label="Total net" value={fmtMoney(d.total_net)} icon={Banknote} accent="slate" />
            <StatCard label="Cotisations à reverser" value={fmtMoney(d.cotisations_a_reverser)} icon={ShieldCheck} accent="amber" />
            <StatCard label="Nets payés" value={fmtMoney(d.nets_payes)} icon={Wallet} accent="emerald" hint={`${d.nb_payes || 0} bulletin(s) payé(s)`} />
            <StatCard label="Restant dû" value={fmtMoney(d.restant_du)} icon={AlertTriangle} accent="rose" />
            <StatCard label="Contrats actifs" value={d.contracts_actifs || 0} icon={Users2} accent="slate" />
          </div>

          <div className="rounded-xl border bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="font-head font-semibold">Reversement des cotisations sociales (CNSS)</h3>
              <p className="text-sm text-slate-500 mt-1">Génère une cotisation à reverser à partir des bulletins <b>validés</b> de {periode} (montant = {fmtMoney(d.cotisations_a_reverser)}).</p>
            </div>
            <Button onClick={generateContribution} disabled={gen} data-testid="generate-contribution" className="bg-brand-600 hover:bg-brand-700 text-white whitespace-nowrap">
              {gen ? "Génération…" : <>Générer la cotisation <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
